import type { Result } from '@orama/orama'
import { useSignal } from '@preact/signals-react'
import { useSignals } from '@preact/signals-react/runtime'
import { useEffect, useRef } from 'react'
import { EVENT_SEE_ITEM } from '../../const'
import * as $ from '../../signal/searchbox'
import type { DocsDocument } from '../../types'
import { highlighter, pathToSection } from '../../utils'
import { FileIcon, HashIcon } from '../Icons/Icons'
import styles from './SearchResult.module.css'

export const SearchResult = ({
  hit,
  isFocused,
  highlight,
  trimText,
  showIcon = true,
  className
}: {
  hit: Result<DocsDocument>
  isFocused: boolean
  highlight?: string
  trimText?: number
  className?: string
  showIcon?: boolean
}) => {
  useSignals()
  const returningProps = useSignal($.searchableProperties).value
  const setResultLink = useSignal($.setResultLink).value
  const keepOpen = useSignal($.keepOpen).value

  const resultContainerRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (!resultContainerRef.current) {
      return
    }

    resultContainerRef.current.scrollIntoView({
      block: 'nearest'
    })
  }, [isFocused, resultContainerRef.current])

  const clickItemHandler = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent> | React.KeyboardEvent<HTMLLIElement>,
    hit: Result<DocsDocument>,
    close = true
  ) => {
    e.stopPropagation()

    const seeItemClicked = new CustomEvent(EVENT_SEE_ITEM, {
      detail: {
        searchTerm: $.searchTerm.value,
        item: {
          ...hit,
          document: {
            ...hit.document,
            path: hit.document[returningProps.value.path],
            title: hit.document[returningProps.value.title],
            ...(returningProps.value.description !== returningProps.value.title
              ? { description: hit.document[returningProps.value.description] }
              : {})
          }
        }
      },
      bubbles: true,
      composed: true
    })
    dispatchEvent(seeItemClicked)

    // Don't close the searchbox if the user is holding the ctrl key
    if (close && !keepOpen.value) {
      if (e.metaKey || e.ctrlKey) {
        return
      }
      setTimeout(() => {
        $.closeAndReset()
      })
    }
  }

  const pathValue = hit.document[returningProps.value.path]?.includes('/')
    ? hit.document[returningProps.value.path]
    : `/${hit.document[returningProps.value.path]}`

  const section = pathToSection(pathValue, hit.document[returningProps.value.title])

  const itemHref = setResultLink.value ? setResultLink.value(hit.document) : pathValue

  const resultTitle = $.setResultTitle.value
    ? $.setResultTitle.value(hit.document)
    : hit.document[returningProps.value.title]

  return (
    <li
      className={[styles.searchbox__result, className].join(' ')}
      aria-current={isFocused}
      onClick={(e) => clickItemHandler(e, hit)}
      onContextMenu={(e) => clickItemHandler(e, hit, false)}
      onKeyDown={(e) => e.key === 'Enter' && clickItemHandler(e, hit)}
    >
      <a
        href={itemHref}
        className={styles.searchbox__result__link}
        data-current-item={isFocused}
        ref={isFocused ? resultContainerRef : null}
      >
        {showIcon && (
          <div className={styles.searchbox__result__icon}>{section.isSubSection ? <HashIcon /> : <FileIcon />}</div>
        )}
        <div>
          {resultTitle ? (
            <h2
              className={styles.searchbox__result__title}
              dangerouslySetInnerHTML={{
                __html: highlighter
                  .highlight(resultTitle, ($.queryTerm.value || $.searchTerm.value) ?? '')
                  .trim(trimText || 50, false)
              }}
            />
          ) : null}

          {hit.document[returningProps.value.description] ? (
            <p
              className={styles.searchbox__result__subtitle}
              dangerouslySetInnerHTML={{
                __html: highlighter
                  .highlight(
                    hit.document[returningProps.value.description],
                    (highlight || $.queryTerm.value || $.searchTerm.value) ?? ''
                  )
                  .trim(trimText || 50)
              }}
            />
          ) : null}

          {$.renderItem?.value?.(hit.document)}
        </div>
      </a>
    </li>
  )
}
